import React from "react";
import { Form } from "react-bootstrap";
import "./AdimplenciaSection.css";
import { IMaskInput } from "react-imask";
import CampoPergunta from "../CampoPergunta/CampoPergunta";
import CampoUpload from "../CampoUpload/CampoUpload";

export interface AssociadoDados {
  cnpj: string;
  nomeSocial: string;
  observacao: string;
}

export interface AdimplenciaSectionProps {
  formaCobranca: string;
  onFormaCobrancaChange: (v: string) => void;
  observacaoGeral: string;
  onObservacaoGeralChange: (v: string) => void;
  quantidadeAssociados: number;
  onQuantidadeAssociadosChange: (n: number) => void;
  tipoDocumento: string;
  onTipoDocumentoChange: (v: string) => void;
  associados: AssociadoDados[];
  maxSizeMB?: number;
  onAssociadoChange: (
    index: number,
    field: keyof AssociadoDados,
    value: string
  ) => void;
  arquivosAssociados: Record<number, string | null>;
  onUploadEscolher: (index: number) => void;
  onUploadRemover: (index: number) => void;
  onUploadFileSelected: (
    index: number,
    file: File,
    options?: { isReplace?: boolean }
  ) => void;
  onUploadDownload?: (index: number) => void;
  uploadDownloadLoadingKey?: string | null;
  uploadProcessingKey?: string | null;
  associadoPodeBaixarArquivo?: (index: number) => boolean;
  observacoesAssociados: Record<number, string>;
  onObservacaoAssociadoChange: (index: number, value: string) => void;
  readOnly?: boolean;
}

const OPCOES_FORMACOBRANCA = [
  { valor: "Mensal" },
  { valor: "Trimestral" },
  { valor: "Semestral" },
  { valor: "Anual" },
];

const OPCOES_QUANTIDADE = [
  { valor: "10", label: "10 ou mais" },
  ...["9", "8", "7", "6", "5", "4", "3", "2", "1"].map((v) => ({
    valor: v,
    label: v,
  })),
];

const cnpjAssociadoIncompleto = (cnpj: string) =>
  String(cnpj || "").replace(/\D/g, "").length !== 14;

const OPCOES_TIPODOC = [
  {
    valor: "RelatorioDebitos",
    label: "Relatório de títulos a receber",
    link: "https://drive.google.com/file/d/13SfUzmX6DdRrue6F8ubPgy9g_itpvJXy/view",
    linkLabel: "(Hi Gestor)",
  },
  {
    valor: "LiquidacaoDebitos",
    label: "Liquidação de títulos",
    link: "https://drive.google.com/file/d/1hNYyNMAGY5Hn0KbhSajKIaaKLN9grKlV/view",
    linkLabel: "(Banco)",
  },
];

const AdimplenciaSection: React.FC<AdimplenciaSectionProps> = ({
  formaCobranca,
  onFormaCobrancaChange,
  observacaoGeral,
  onObservacaoGeralChange,
  quantidadeAssociados,
  onQuantidadeAssociadosChange,
  tipoDocumento,
  onTipoDocumentoChange,
  associados,
  maxSizeMB,
  onAssociadoChange,
  arquivosAssociados,
  onUploadEscolher,
  onUploadRemover,
  onUploadFileSelected,
  onUploadDownload,
  uploadDownloadLoadingKey,
  uploadProcessingKey,
  associadoPodeBaixarArquivo,
  observacoesAssociados,
  onObservacaoAssociadoChange,
  readOnly = false,
}) => {
  const quantidade = Math.max(0, Math.min(10, quantidadeAssociados));
  const tamanhoMaxMB =
    Number.isFinite(maxSizeMB as number) && (maxSizeMB as number) > 0
      ? (maxSizeMB as number)
      : 300;

  return (
    <>
      <CampoPergunta
        name="adimplencia-formaCobranca"
        pergunta="Qual é a forma de cobrança da mensalidade associativa do Sindicato?"
        opcoes={OPCOES_FORMACOBRANCA}
        valor={formaCobranca}
        onChange={onFormaCobrancaChange}
        disabled={readOnly}
        highlightRequired={!String(formaCobranca || "").trim()}
      />

      <Form.Group className="mt-3">
        <Form.Label className="fw-bold">Observação:</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          placeholder="Adicione observações caso necessário."
          value={observacaoGeral}
          onChange={(e) => onObservacaoGeralChange(e.target.value)}
          disabled={readOnly}
        />
      </Form.Group>

      <CampoPergunta
        name="adimplencia-quantidade"
        pergunta="Quantas empresas associadas tem seu Sindicato?"
        opcoes={OPCOES_QUANTIDADE}
        valor={quantidadeAssociados > 0 ? String(quantidadeAssociados) : ""}
        onChange={(v) => onQuantidadeAssociadosChange(parseInt(v, 10) || 0)}
        disabled={readOnly}
        highlightRequired={quantidadeAssociados <= 0}
      />

      <Form.Group
        className={`mt-3${!String(tipoDocumento || "").trim() ? " adimplencia-tipo-doc--required" : ""}`}
      >
        <Form.Label className="fw-bold">Tipo do documento?</Form.Label>
        <div className="d-flex flex-column gap-2">
          {OPCOES_TIPODOC.map((op) => (
            <div
              key={op.valor}
              className="d-flex align-items-center gap-2 flex-wrap"
            >
              <Form.Check
                type="radio"
                name="adimplencia-tipoDocumento"
                id={`adimplencia-tipo-${op.valor}`}
                label={op.label}
                value={op.valor}
                checked={tipoDocumento === op.valor}
                onChange={() => onTipoDocumentoChange(op.valor)}
                disabled={readOnly}
              />
              {op.link && (
                <a
                  href={op.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="small text-primary"
                  onClick={(e) => e.stopPropagation()}
                >
                  {op.linkLabel ?? "Ver modelo"}
                </a>
              )}
            </div>
          ))}
        </div>
      </Form.Group>

      {quantidade > 0 &&
        associados.slice(0, quantidade).map((assoc, idx) => {
          const num = idx + 1;
          const labelNum = num.toString().padStart(2, "0");
          const chaveAssoc = `adimplencia-assoc-${idx}`;
          const destacarCnpj = cnpjAssociadoIncompleto(assoc.cnpj);
          const destacarNome = !String(assoc.nomeSocial || "").trim();
          const destacarArquivo =
            uploadProcessingKey !== chaveAssoc &&
            !String(arquivosAssociados[idx] ?? "").trim() &&
            !(associadoPodeBaixarArquivo?.(idx) ?? false);

          return (
            <div
              key={num}
              className="p-3 mt-4 border border-secondary rounded bg-light"
              style={{ borderWidth: 2 }}
            >
              <h6 className="fw-bold mb-3">Associado {labelNum}</h6>

              <Form.Group className="mt-2">
                <Form.Label className="fw-bold">CNPJ</Form.Label>
                <IMaskInput
                  mask="00.000.000/0000-00"
                  value={assoc.cnpj}
                  onAccept={(value) =>
                    onAssociadoChange(idx, "cnpj", String(value))
                  }
                  className={`form-control${destacarCnpj ? " is-invalid" : ""}`}
                  placeholder="00.000.000/0000-00"
                  disabled={readOnly}
                />
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label className="fw-bold">Nome social</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite a razão social"
                  value={assoc.nomeSocial}
                  onChange={(e) =>
                    onAssociadoChange(idx, "nomeSocial", e.target.value)
                  }
                  isInvalid={destacarNome}
                  disabled={readOnly}
                />
              </Form.Group>

              <CampoUpload
                instrucao={`O arquivo deve estar em formato PDF do tamanho ${tamanhoMaxMB}MB. Clique no botão abaixo para anexar`}
                valorArquivo={arquivosAssociados[idx] ?? null}
                onEscolher={() => onUploadEscolher(idx)}
                onRemover={() => onUploadRemover(idx)}
                onFileSelected={(file, options) =>
                  onUploadFileSelected(idx, file, options)
                }
                onDownload={
                  onUploadDownload &&
                  (associadoPodeBaixarArquivo
                    ? associadoPodeBaixarArquivo(idx)
                    : Boolean(arquivosAssociados[idx]))
                    ? () => onUploadDownload(idx)
                    : undefined
                }
                downloadLoading={
                  uploadDownloadLoadingKey === `adimplencia-assoc-${idx}`
                }
                uploadPending={
                  uploadProcessingKey === `adimplencia-assoc-${idx}`
                }
                highlightRequiredEmpty={destacarArquivo}
                maxSizeMB={tamanhoMaxMB}
                accept=".pdf"
                disabled={readOnly}
                disabledObservacao={readOnly}
                observacao={observacoesAssociados[idx] ?? ""}
                onObservacaoChange={(v) =>
                  onObservacaoAssociadoChange(idx, v)
                }
              />
            </div>
          );
        })}
    </>
  );
};

export default AdimplenciaSection;